import { useRef, useState } from 'react'
import { syncService } from '@core/services/sync/SyncService'
import { ExcelParser } from '@utils/index'
import type { ParsedRow } from '@utils/index'
import { useAuthToken } from './useAuthToken'
import { CLOUD_API } from './shared'
import { btn, btnBase, btnSize, cx, modalLg, overlay } from '@ui/classes'
import { errBanner, okBanner, wizardApplyBtn } from '@ui/admin'

import { httpErrorText } from '@core/utils/httpError'
import { Upload } from '@ui/icons'
/**
 * Публикация каталога в облако: импорт прайса (3 режима) и публикация версии
 * для касс. Живёт в разделе «Каталог» — это операция над каталогом.
 */
export function CloudCatalogPublishSection() {
  const token = useAuthToken()
  const [importMode, setImportMode] = useState<'merge' | 'prices_only' | 'full_replace'>('merge')
  // Мастер импорта: распарсенные строки ждут подтверждения режима директором.
  const [importWizard, setImportWizard] = useState<{
    fileName: string
    rows: ParsedRow[]
    categoriesCount: number
  } | null>(null)
  const cloudFileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  /** Выбор файла прайса: парсим и открываем Мастер импорта (без отправки). */
  const onCloudExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setErr(null)
    setNote(null)
    const file = e.target.files?.[0]
    if (!file || !token) return
    try {
      const buffer = await file.arrayBuffer()
      const parsed = new ExcelParser().parse(buffer)
      const categoriesCount = new Set(
        parsed.rows.map((r) => r.category?.trim() || 'Общее')
      ).size
      setImportMode('merge')
      setImportWizard({ fileName: file.name, rows: parsed.rows, categoriesCount })
    } catch (e2: unknown) {
      setErr(e2 instanceof Error ? e2.message : String(e2))
    } finally {
      if (cloudFileRef.current) cloudFileRef.current.value = ''
    }
  }

  /** Применить импорт из Мастера: отправить в облако в выбранном режиме. */
  const runImport = async () => {
    if (!importWizard || !token) return
    setBusy(true)
    setErr(null)
    try {
      const products = importWizard.rows.map((r) => ({
        barcode: r.barcode ?? '',
        name: r.name,
        price: r.price,
        stock: r.stock ?? 0,
        requires_marking: Boolean(r.requiresMarking),
        category: r.category ?? 'Общее',
        is_weight: r.isWeight ?? r.unit === 'кг',
        unit: r.unit ?? 'шт'
      }))
      const resp = await fetch(`${CLOUD_API}/catalog/products/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ products, mode: importMode })
      })
      if (!resp.ok) {
        setErr(httpErrorText('Импорт каталога', resp.status))
        return
      }
      const data = (await resp.json()) as {
        created_count?: number
        price_updated_count?: number
        field_updated_count?: number
        unchanged_count?: number
        total_in_file?: number
        imported_count: number
        catalog_version_id: number
        checkpoint?: { id: number; kind: 'major' | 'minor' }
      }
      const checkpointText = data.checkpoint
        ? data.checkpoint.kind === 'major'
          ? ` Создана версия №${data.checkpoint.id} (крупные изменения).`
          : ` Создана подверсия №${data.checkpoint.id}.`
        : ''
      setNote(
        `Обработано ${data.total_in_file ?? products.length} позиций: ` +
          `цен обновлено: ${data.price_updated_count ?? 0}, ` +
          `прочих правок: ${data.field_updated_count ?? 0}, ` +
          `добавлено новых: ${data.created_count ?? data.imported_count}, ` +
          `без изменений: ${data.unchanged_count ?? 0}.` +
          checkpointText
      )
      setImportWizard(null)
    } catch (e2: unknown) {
      setErr(e2 instanceof Error ? e2.message : String(e2))
    } finally {
      setBusy(false)
    }
  }

  /** Публикация новой версии каталога для касс. */
  const publishVersion = async () => {
    if (!token) return
    setBusy(true)
    setErr(null)
    setNote(null)
    try {
      const resp = await fetch(`${CLOUD_API}/catalog/versions/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      })
      if (!resp.ok) {
        setErr(httpErrorText('Публикация каталога', resp.status))
        return
      }
      const data = (await resp.json()) as {
        published_version: { id: number; version_name: string }
      }
      setNote(
        `Опубликована версия №${data.published_version.id} «${data.published_version.version_name}». Кассы подтянут её при синхронизации.`
      )
      void syncService.syncAll().catch(() => undefined)
    } catch (e2: unknown) {
      setErr(e2 instanceof Error ? e2.message : String(e2))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      {note && <p className={okBanner}>{note}</p>}
      {err && <p className={errBanner}>{err}</p>}

      {!token ? (
        <p className="m-0 text-small text-muted">
          Подключите облако в разделе «Интеграции», чтобы импортировать и публиковать каталог.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-4">
          <label className="text-small">
            Импорт прайса в облако (.xlsx / .xls / .ods / .csv):
            <input
              ref={cloudFileRef}
              type="file"
              accept=".xlsx,.xls,.ods,.csv,.tsv"
              onChange={onCloudExcel}
              disabled={busy}
              className="mt-1 block w-full cursor-pointer rounded-card border border-lineStrong bg-white px-2.5 py-2 text-sm"
            />
          </label>
          <button onClick={() => void publishVersion()} disabled={busy} className={cx(btnBase, btn.secondary, btnSize.sm)}>
            Опубликовать каталог для касс
          </button>
        </div>
      )}

      {/* Мастер импорта прайса (подтверждение режима перед отправкой) */}
      {importWizard && (
        <div className={cx(overlay, 'z-modal')} onClick={() => !busy && setImportWizard(null)}>
          <div className={modalLg} onClick={(e) => e.stopPropagation()}>
            <h4 className="mt-0 text-base2 font-bold">Импорт каталога товаров</h4>
            <p className="text-small text-inkSoft">
              Файл: <strong>{importWizard.fileName}</strong> · Строк:{' '}
              <strong>{importWizard.rows.length}</strong> · Категорий:{' '}
              <strong>{importWizard.categoriesCount}</strong>
            </p>
            <div className="my-3 flex flex-col gap-2">
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="wizardMode"
                  checked={importMode === 'merge'}
                  onChange={() => setImportMode('merge')}
                 className="h-4 w-4 cursor-pointer accent-ok"/>
                <span>Слияние (добавить новые + обновить)</span>
              </label>
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="wizardMode"
                  checked={importMode === 'prices_only'}
                  onChange={() => setImportMode('prices_only')}
                 className="h-4 w-4 cursor-pointer accent-ok"/>
                <span>Только цены (обновить цены у существующих товаров)</span>
              </label>
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="wizardMode"
                  checked={importMode === 'full_replace'}
                  onChange={() => setImportMode('full_replace')}
                 className="h-4 w-4 cursor-pointer accent-ok"/>
                <span>Полная замена каталога (архивировать старые)</span>
              </label>
            </div>
            <div className="flex gap-2">
              <button className={wizardApplyBtn} onClick={() => void runImport()} disabled={busy}>
                {busy ? 'Отправка…' : (<><Upload size={18} /> Применить и обновить каталог</>)}
              </button>
              <button onClick={() => setImportWizard(null)} disabled={busy} className={cx(btnBase, btn.secondary, btnSize.sm)}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
